import { ColumnDef } from '@tanstack/react-table';
import { CheckCircle, Edit, Eye, MoreHorizontal, Package, Plus, Trash2, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AssetTemplate } from '@/types/assetTemplate';
import AssetTemplateForm from '../components/AssetTemplateForm';
import Header from '../components/Header';
import { ProductThumbnail } from '../components/ui/aspect-ratio-image';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createBadgeColumn, createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fetchAllAssetCategories } from '../lib/assetCategories';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { getSupplierStock } from '../utils/supplierStock';

type Role = { key: string; name: string; description: string };
type User = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };
type AssetCategory = { id: string; name: string };

type ProductFilter = 'all' | 'active';
type ConditionFilter = 'pre_loved' | 'not_pre_loved';

interface AssetsProps {
  conditionFilter?: ConditionFilter;
}

const PAGE_TITLES: Record<ConditionFilter | 'all', string> = {
  all: 'Products',
  pre_loved: 'Pre-Loved Products',
  not_pre_loved: 'New Products',
};

const CURRENT_PAGES: Record<ConditionFilter | 'all', string> = {
  all: 'assets',
  pre_loved: 'assets-pre-loved',
  not_pre_loved: 'assets-new',
};

const Assets: React.FC<AssetsProps> = ({ conditionFilter }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState<AssetTemplate[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productFilter, setProductFilter] = useState<ProductFilter>('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AssetTemplate | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Search state (server-side)
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Category filter (server-side) - init from URL on mount
  const [categoryId, setCategoryId] = useState<string>(() => searchParams.get('category_id') || '');
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const debouncedColor = useDebouncedValue(color, 300);
  const debouncedSize = useDebouncedValue(size, 300);

  // Stats from API (across all products, not just current page)
  const [stats, setStats] = useState({
    totalUnits: 0,
    totalTemplates: 0,
    totalActiveTemplates: 0,
    totalRentedUnits: 0,
  });

  // Delete/Toggle dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<AssetTemplate | null>(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [templateToToggle, setTemplateToToggle] = useState<AssetTemplate | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadTemplates();
    checkAuth();
  }, [sortBy, sortOrder, page, limit, debouncedSearch, categoryId, productFilter, conditionFilter, debouncedColor, debouncedSize]);

  useEffect(() => {
    (async () => {
      try {
        setCategories(await fetchAllAssetCategories(false));
      } catch {}
    })();
  }, []);

  // Lazy load stats on initial mount only (separate API call)
  useEffect(() => {
    loadStats();
  }, []);

  const handleFilterClick = (filter: ProductFilter) => {
    setProductFilter(filter);
    setPage(1);
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Fetch products with pagination (no stats - much faster!)
      const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
      const categoryParam = categoryId ? `&category_id=${encodeURIComponent(categoryId)}` : '';
      const conditionParam = conditionFilter === 'pre_loved'
        ? '&default_condition=pre_loved'
        : conditionFilter === 'not_pre_loved'
          ? '&exclude_default_condition=pre_loved'
          : '';
      const includeInactive = productFilter === 'all';
      const colorParam = debouncedColor ? `&color=${encodeURIComponent(debouncedColor)}` : '';
      const sizeParam = debouncedSize ? `&size=${encodeURIComponent(debouncedSize)}` : '';
      const response = await fetch(`/api/asset-templates?page=${page}&limit=${limit}&include_inactive=${includeInactive}&sortBy=${sortBy}&sortOrder=${sortOrder}${searchParam}${categoryParam}${conditionParam}${colorParam}${sizeParam}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load asset templates');
      }

      const data = await response.json();

      // Handle response format: { success: true, data: templates[], pagination: {...} }
      if (data.success && data.pagination) {
        setTemplates(data.data);
        setTotalProducts(data.pagination.total);
        setTotalPages(data.pagination.pages);  // Backend returns 'pages' not 'totalPages'
      } else if (data.success) {
        // Fallback for non-paginated response
        const templatesData = Array.isArray(data.data) ? data.data : data;
        setTemplates(templatesData);
        setTotalProducts(templatesData.length);
        setTotalPages(1);
      } else {
        // Legacy format or error
        const templatesData = Array.isArray(data) ? data : [];
        setTemplates(templatesData);
        setTotalProducts(templatesData.length);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/asset-templates/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setStats(data.data);
        }
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      // Non-critical error - don't show to user
    }
  };

  const checkAuth = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    } catch (err) {
      console.error('Error reading user data:', err);
    }
  };

  const startCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const startEdit = (template: AssetTemplate) => {
    setEditing(template);
    setDialogOpen(true);
  };

  const viewDetails = (template: AssetTemplate) => {
    navigate(`/assets/${template.id}`);
  };

  const handleFormSuccess = () => {
    setDialogOpen(false);
    setEditing(null);
    loadTemplates();
    loadStats();
  };

  const handleDeleteClick = (template: AssetTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-templates/${templateToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete template');
      }

      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      loadTemplates();
      loadStats();
    } catch (err) {
      console.error('Error deleting template:', err);
      setDeleteDialogOpen(false);
      setErrorMessage('Failed to delete template. Please try again.');
      setErrorDialogOpen(true);
    }
  };

  const handleToggleClick = (template: AssetTemplate) => {
    setTemplateToToggle(template);
    setToggleDialogOpen(true);
  };

  const toggleActive = async () => {
    if (!templateToToggle) return;

    const newStatus = !templateToToggle.is_active;
    const action = newStatus ? 'activate' : 'deactivate';

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-templates/${templateToToggle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} template`);
      }

      setToggleDialogOpen(false);
      setTemplateToToggle(null);
      loadTemplates();
      loadStats();
    } catch (err) {
      console.error(`Error ${action} template:`, err);
      setToggleDialogOpen(false);
      setErrorMessage(`Failed to ${action} template. Please try again.`);
      setErrorDialogOpen(true);
    }
  };

  const handleSortingChange = (newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when sorting changes
  };


  const canEdit = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key)) ?? false;
  const canDelete = user?.roles?.some(role => role.key === 'admin') ?? false;

  // Use stats from API instead of calculating from current page
  const getTotalInstances = () => stats.totalUnits;
  const getTotalTemplates = () => stats.totalTemplates;
  const getActiveTemplates = () => stats.totalActiveTemplates;
  const getRentedUnits = () => stats.totalRentedUnits;

  const columns: ColumnDef<AssetTemplate>[] = [
    {
      accessorKey: 'name',
      header: 'Product Name',
      enableSorting: true,
      cell: ({ row }) => {
        const template = row.original as AssetTemplate & { images?: Array<{ url: string }> };
        const img = template.images?.[0]?.url;
        return (
          <div className="flex items-center gap-3">
            {img ? (
              <ProductThumbnail src={img} alt={template.name} size="sm" fallback={<Package className="h-6 w-6 text-muted-foreground" />} />
            ) : (
              <ProductThumbnail src={null} alt={template.name} size="sm" fallback={<Package className="h-6 w-6 text-muted-foreground" />} />
            )}
            <button
              onClick={() => viewDetails(template)}
              className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
            >
              {template.name}
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: 'category.name',
      header: 'Category',
      enableSorting: true,
      cell: ({ row }) => {
        const template = row.original;
        return template.category ? (
          <Badge variant="secondary">{template.category.name}</Badge>
        ) : (
          <span className="text-gray-400 italic">No category</span>
        );
      },
    },
    {
      accessorKey: 'template_code',
      header: 'Product Code',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="font-mono text-sm text-gray-700">{row.original.template_code}</span>
      ),
    },
    {
      accessorKey: 'base_price',
      header: 'Base Price',
      enableSorting: true,
      cell: ({ row }) => {
        const template = row.original;
        return template.base_price != null ? (
          <span className="font-medium text-gray-900">
            £{Number(template.base_price).toFixed(2)}
          </span>
        ) : (
          <span className="text-gray-400 italic">N/A</span>
        );
      },
    },
    {
      id: 'supplier_stock',
      header: 'Supplier Stock',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-center">
          <span className="text-lg font-bold text-gray-900">
            {getSupplierStock(row.original)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'warehouse_stock',
      header: 'Warehouse Stock',
      cell: ({ row }) => {
        const template = row.original as AssetTemplate;
        const stock = template.warehouse_stock;
        return (
          <div className="text-center">
            <span className="text-lg font-bold text-gray-900">
              {stock == null ? '—' : stock}
            </span>
          </div>
        );
      },
    },
    createBadgeColumn<AssetTemplate>('default_condition', 'Default Condition', {
      getBadgeVariant: (value) => {
        const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'warning'> = {
          new: 'default',
          pre_loved: 'secondary',
          damaged: 'destructive',
          missing: 'warning',
        };
        return variants[value] || 'secondary';
      },
      formatValue: (value) =>
        String(value)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase()),
    }),
    createBadgeColumn<AssetTemplate>('is_active', 'Status', {
      getBadgeVariant: (value) => {
        const isActive = typeof value === 'boolean' ? value : value === 'true';
        return isActive ? 'default' : 'secondary';
      },
      formatValue: (value) => {
        const isActive = typeof value === 'boolean' ? value : value === 'true';
        return isActive ? 'Active' : 'Inactive';
      },
    }),
    createDateColumn<AssetTemplate>('created_at', 'Created At', 'datetime'),
    {
      accessorKey: 'created_by_user',
      header: 'Created By',
      cell: ({ row }) => {
        const template = row.original;
        return template.created_by_user
          ? `${template.created_by_user.first_name} ${template.created_by_user.last_name}`
          : 'System';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableHiding: false,
      cell: ({ row }) => {
        const template = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => viewDetails(template)}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={() => startEdit(template)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Product
                </DropdownMenuItem>
              )}
              {canEdit && (
                <DropdownMenuItem onClick={() => handleToggleClick(template)}>
                  {template.is_active ? (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Activate
                    </>
                  )}
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => handleDeleteClick(template)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Product
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const pageKey = conditionFilter ?? 'all';

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage={CURRENT_PAGES[pageKey]} />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-heading font-bold text-primary leading-tight mb-2">
            {PAGE_TITLES[pageKey]}
          </h2>
        </div>

        {/* Space before table toolbar */}
        <div className="mb-2" />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => handleFilterClick('all')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              productFilter === 'all' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">{getTotalTemplates()}</div>
            <div className="text-sm text-muted-foreground">Total Products</div>
          </div>
          <div
            onClick={() => handleFilterClick('active')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              productFilter === 'active' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">{getActiveTemplates()}</div>
            <div className="text-sm text-muted-foreground">Active Products</div>
          </div>
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{getTotalInstances()}</div>
            <div className="text-sm text-muted-foreground">Total Units</div>
          </div>
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-purple-600">
              {getTotalInstances() > 0 ? Math.round((getRentedUnits() / getTotalInstances()) * 100) + '%' : '0%'}
            </div>
            <div className="text-sm text-muted-foreground">Utilization (Rented/Total)</div>
          </div>
        </div>

        {/* Products Table */}
        <DataTable
          columns={columns}
          data={templates}
          loading={loading}
          searchKey="name"
          searchPlaceholder="Search products or categories..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          serverSide={true}
          pagination={{
            total: totalProducts,
            page,
            limit,
            pages: totalPages,
          }}
          onPaginationChange={(newPage, newLimit) => {
            if (newLimit !== limit) {
              setLimit(newLimit);
              setPage(1); // Reset to first page when limit changes
            } else {
              setPage(newPage);
            }
          }}
          onSortingChange={handleSortingChange}
          toolbarContentLeft={
            <div className="flex flex-wrap gap-2">
            <Select value={categoryId || '__all__'} onValueChange={(v) => { setCategoryId(v === '__all__' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              className="border rounded-md px-3 py-2 text-sm w-28"
              placeholder="Color"
              value={color}
              onChange={(e) => { setColor(e.target.value); setPage(1); }}
            />
            <input
              className="border rounded-md px-3 py-2 text-sm w-28"
              placeholder="Size"
              value={size}
              onChange={(e) => { setSize(e.target.value); setPage(1); }}
            />
            </div>
          }
          toolbarContentRight={
            <div className="flex gap-2">
              {canEdit && (
                <Button onClick={startCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Product
                </Button>
              )}
            </div>
          }
          emptyMessage="No products found. Create your first product to get started."
        />

        {/* Create/Edit Product Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editing ? 'Edit Product' : 'Create New Product'}
              </DialogTitle>
              <DialogDescription>
                {editing
                  ? `Update details for "${editing.name}"`
                  : 'Create a new product for your catalog'
                }
              </DialogDescription>
            </DialogHeader>
            <AssetTemplateForm
              template={editing}
              onSuccess={handleFormSuccess}
              onCancel={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Product"
          description={`Are you sure you want to delete "${templateToDelete?.name}"? This will also delete all associated instances. This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDelete}
          variant="destructive"
        />

        {/* Toggle Active Confirmation Dialog */}
        <ConfirmDialog
          open={toggleDialogOpen}
          onOpenChange={setToggleDialogOpen}
          title={templateToToggle?.is_active ? 'Deactivate Product' : 'Activate Product'}
          description={`Are you sure you want to ${templateToToggle?.is_active ? 'deactivate' : 'activate'} "${templateToToggle?.name}"?`}
          confirmText={templateToToggle?.is_active ? 'Deactivate' : 'Activate'}
          cancelText="Cancel"
          onConfirm={toggleActive}
          variant={templateToToggle?.is_active ? 'destructive' : 'default'}
        />

        {/* Error Message Dialog */}
        <MessageDialog
          open={errorDialogOpen}
          onOpenChange={setErrorDialogOpen}
          title="Error"
          description={errorMessage}
          variant="destructive"
        />
      </main>
    </div>
  );
};

export default Assets;
